import { api } from '../shared/api/client'
import type {
  UserRegister,
  UserLogin,
  Token,
  UserResponse,
  CompanyResponse,
  UserUpdateRequest,
  PasswordChangeRequest,
  UserListResponse,
  TenantStats,
  ApiResponse
} from '../types/auth.types'

// 认证服务
export class AuthService {
  // 用户注册
  async register(userData: UserRegister): Promise<{
    user: UserResponse
    company: CompanyResponse
    token: Token
  }> {
    try {
      const response = await api.post<{
        user: UserResponse
        company: CompanyResponse
        token: Token
      }>('/auth/register', userData)

      // 存储令牌到localStorage
      if (response.token) {
        localStorage.setItem('access_token', response.token.access_token)
        if (response.token.refresh_token) {
          localStorage.setItem('refresh_token', response.token.refresh_token)
        }
      }

      return response
    } catch (error) {
      console.error('注册失败:', error)
      throw error
    }
  }

  // 用户登录
  async login(loginData: UserLogin): Promise<{
    user: UserResponse
    company: CompanyResponse
    token: Token
  }> {
    try {
      const response = await api.post<{
        user: UserResponse
        company: CompanyResponse
        token: Token
      }>('/auth/login', loginData)

      // 存储令牌到localStorage
      if (response.token) {
        localStorage.setItem('access_token', response.token.access_token)
        if (response.token.refresh_token) {
          localStorage.setItem('refresh_token', response.token.refresh_token)
        }
      }

      return response
    } catch (error) {
      console.error('登录失败:', error)
      throw error
    }
  }

  // 刷新访问令牌
  async refreshToken(): Promise<Token> {
    try {
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        throw new Error('没有可用的刷新令牌')
      }

      const response = await api.post<Token>('/auth/refresh', {
        refresh_token: refreshToken
      })

      // 更新存储的令牌
      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token)
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token)
        }
      }

      return response
    } catch (error) {
      console.error('刷新令牌失败:', error)
      // 清除无效的令牌
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      throw error
    }
  }

  // 退出登录
  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        await api.post('/auth/logout', {
          refresh_token: refreshToken
        })
      }
    } catch (error) {
      console.error('退出登录失败:', error)
    } finally {
      // 无论如何都清除本地存储的令牌
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<{
    user: UserResponse
    company: CompanyResponse
  }> {
    try {
      // API 拦截器已经处理了 data 提取，直接返回的就是 {user, company}
      const response = await api.get<{
        user: UserResponse
        company: CompanyResponse
      }>('/auth/me')

      if (!response || !response.user || !response.company) {
        throw new Error('获取用户信息失败：未返回数据')
      }

      return response
    } catch (error) {
      console.error('获取用户信息失败:', error)
      throw error
    }
  }

  // 验证令牌
  async validateToken(): Promise<boolean> {
    try {
      await api.get('/auth/validate')
      return true
    } catch (error) {
      console.error('令牌验证失败:', error)
      return false
    }
  }

  // 更新用户信息
  async updateUser(userId: number, updateData: UserUpdateRequest): Promise<UserResponse> {
    try {
      const response = await api.put<UserResponse>(
        `/users/${userId}`,
        updateData
      )
      return response
    } catch (error) {
      console.error('更新用户信息失败:', error)
      throw error
    }
  }

  // 修改密码
  async changePassword(userId: number, passwordData: PasswordChangeRequest): Promise<void> {
    try {
      await api.post<void>(
        `/users/${userId}/password`,
        passwordData
      )
    } catch (error) {
      console.error('修改密码失败:', error)
      throw error
    }
  }

  // 获取用户列表（管理员功能）
  async getUsers(params?: {
    page?: number
    per_page?: number
    search?: string
    role_filter?: string
    active_only?: boolean
  }): Promise<UserListResponse> {
    try {
      const response = await api.get<UserListResponse>('/users', { params })
      return response
    } catch (error) {
      console.error('获取用户列表失败:', error)
      throw error
    }
  }

  // 获取用户详情
  async getUserById(userId: number): Promise<UserResponse> {
    try {
      const response = await api.get<UserResponse>(`/users/${userId}`)
      return response
    } catch (error) {
      console.error('获取用户详情失败:', error)
      throw error
    }
  }

  // 删除用户（管理员功能）
  async deleteUser(userId: number): Promise<void> {
    try {
      await api.delete<void>(`/users/${userId}`)
    } catch (error) {
      console.error('删除用户失败:', error)
      throw error
    }
  }

  // 激活/停用用户（管理员功能）
  async toggleUserActive(userId: number, active: boolean): Promise<UserResponse> {
    try {
      const endpoint = active ? 'activate' : 'deactivate'
      const response = await api.post<UserResponse>(
        `/users/${userId}/${endpoint}`
      )
      return response
    } catch (error) {
      console.error(`${active ? '激活' : '停用'}用户失败:`, error)
      throw error
    }
  }

  // 获取租户统计信息
  async getTenantStats(companyId: number): Promise<TenantStats> {
    try {
      const response = await api.get<TenantStats>(
        `/companies/${companyId}/stats`
      )
      return response
    } catch (error) {
      console.error('获取租户统计信息失败:', error)
      throw error
    }
  }

  // 获取公司信息
  async getCompany(companyId: number): Promise<CompanyResponse> {
    try {
      const response = await api.get<CompanyResponse>(
        `/companies/${companyId}`
      )
      return response
    } catch (error) {
      console.error('获取公司信息失败:', error)
      throw error
    }
  }

  // 更新公司信息
  async updateCompany(companyId: number, updateData: {
    name?: string
    description?: string
  }): Promise<CompanyResponse> {
    try {
      const response = await api.put<CompanyResponse>(
        `/companies/${companyId}`,
        updateData
      )
      return response
    } catch (error) {
      console.error('更新公司信息失败:', error)
      throw error
    }
  }

  // 检查令牌是否有效（静默检查）
  async checkTokenValidity(): Promise<boolean> {
    const token = localStorage.getItem('access_token')
    if (!token) {
      return false
    }

    try {
      // 使用一个轻量级的端点检查令牌
      await api.get('/auth/validate', {
        timeout: 5000 // 5秒超时
      })
      return true
    } catch (error) {
      // 尝试刷新令牌
      try {
        await this.refreshToken()
        return true
      } catch (refreshError) {
        return false
      }
    }
  }

  // 获取存储的令牌
  getStoredToken(): string | null {
    return localStorage.getItem('access_token')
  }

  // 获取存储的刷新令牌
  getStoredRefreshToken(): string | null {
    return localStorage.getItem('refresh_token')
  }

  // 清除所有存储的令牌
  clearTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }
}

// 导出单例实例
export const authService = new AuthService()